/* eslint-disable complexity */
import { useCallback, useContext, useEffect } from 'react';
import API from '@scale/frontend-lib/dashboard-api';
import {
  IndexedInteractionStepResponse,
  InteractionStepResponse,
  InteractionStepType,
  StepParamsForType,
  sanitizeStepResponses,
} from '@scale/shared/types/chat';
import { TEST_CHAT_SUBTASK } from '@scale/shared/defaults';
import {
  LintOutcome,
  NormalLintResult,
  ScriptLinterMetadata,
  getValidLintResults,
  IProcessedLintResults,
} from '@scale/shared/types/linter';
import { logEvent } from '@scale/frontend-lib/events';
import { Event } from '@scale/shared/types/events/index';
import { LinterDismissEvent } from '@scale/shared/types/events/LinterDismissEvent';
import { GenAILinterFeedbackEvent } from '@scale/shared/types/events/GenAILinterFeedbackEvent';
import { getMostRecentPrompt, getMostRecentResponse } from '@scale/shared/chat/response';
import { defaultApiFetchErrorHandler } from 'react-scale/lib/api-fetch';
import logger from '@scale/frontend-lib/logging';

import _ from 'lodash';
import produce from 'immer';
import { useChatStoreRef } from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import { ChatTaskContext } from '../../contexts/ChatTaskContext';
import { useEventBus, ChatEventBus } from '../../events';
import { computeShouldSkipLinting } from 'react-scale/components/scalers/tasks/ThreadedInteraction/utils';
import { getCanSubmit } from './getCanSubmit';
import type { LintMessage } from './LintMessage';

export type PerStepResponseLinterHook = ReturnType<typeof usePerStepResponseLinter>;

const isValidLintResult = (
  lintResult: NormalLintResult<ScriptLinterMetadata<any>>,
  isTestEnvironment: boolean,
): boolean => {
  if (isTestEnvironment) {
    return !!lintResult.frontendSeverity && lintResult.lintOutcome === LintOutcome.Fail;
  }
  return !!lintResult.frontendSeverity;
};

const handleProcessedLintResponse = ({
  response,
  currentResponseIndex,
  setHasLinterErrorForIndex,
  setHasIncompleteFrontendLinterFeedbackForIndex,
  setLintMessages,
  setParamsAtLastCheck,
  eventBus,
  assignmentId,
}: {
  response: IProcessedLintResults;
  currentResponseIndex: number;
  setHasLinterErrorForIndex: (index: number, value: boolean) => void;
  setHasIncompleteFrontendLinterFeedbackForIndex: (index: number, value: boolean) => void;
  setLintMessages: (value: LintMessage[] | ((prev: LintMessage[]) => LintMessage[])) => void;
  setParamsAtLastCheck: (value: any) => void;
  eventBus: ChatEventBus;
  assignmentId?: string;
}): { canSubmit: boolean; newJsonataContextList: any } => {
  const { lintResults } = response;
  const flattenedLintResults = _.values(lintResults)
    .flat()
    .filter((l): l is NormalLintResult<ScriptLinterMetadata<any>> => !!l);

  const newJsonataContextList = flattenedLintResults.filter(l => l.metadata?.jsonataContext);

  // In linters playground page, we receive all lint results from the backend, not only the `pass` ones
  // To prevent false-positive linter popup messages, we filter them out here to keep only `fail` ones
  const isTestEnvironment = assignmentId === TEST_CHAT_SUBTASK;

  const validLintResults = getValidLintResults(flattenedLintResults).filter(lintResult =>
    isValidLintResult(lintResult, isTestEnvironment),
  );

  const newLintMessages: LintMessage[] = validLintResults.map(validLintResult => ({
    severity: validLintResult.frontendSeverity!,
    lintRuleName: validLintResult.name,
    lintRuleMessage: validLintResult.message,
    linterMessage: validLintResult.metadata?.message,
    forceLinterFeedback: !!(
      validLintResult.frontendLinterFeedbackRate &&
      Math.random() < validLintResult.frontendLinterFeedbackRate / 100
    ),
    feedback: undefined,
    genAILinterEventRowId: validLintResult.metadata?.genAILinterEventRowId,
  }));

  try {
    newLintMessages.forEach(lintMessage => {
      eventBus.emit('linter:result', {
        severity: lintMessage.severity,
        ruleName: lintMessage.lintRuleName,
        ruleMessage: lintMessage.lintRuleMessage,
        linterMessage: lintMessage.linterMessage,
        forceLinterFeedback: lintMessage.forceLinterFeedback,
        genAILinterEventRowId: lintMessage.genAILinterEventRowId,
      });
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Failed to put linter:result to the Event Bus', err);
  }

  const { canSubmit, passLinters, allLinterFeedbackCompleted } = getCanSubmit(newLintMessages);
  if (!passLinters) setHasLinterErrorForIndex(currentResponseIndex, true);
  if (!allLinterFeedbackCompleted)
    setHasIncompleteFrontendLinterFeedbackForIndex(currentResponseIndex, true);
  if (canSubmit) {
    setLintMessages([]);
    setParamsAtLastCheck(undefined);
  } else {
    setLintMessages(newLintMessages);
  }

  return { canSubmit, newJsonataContextList };
};

export const usePerStepResponseLinter = () => {
  const taskContext = useContext(ChatTaskContext);
  const eventBus = useEventBus();
  const { projectId, workerId, assignmentId, disableLinter, linterPlaygroundMode } = taskContext;

  const chatStoreRef = useChatStoreRef();

  const setFeedback = useCallback(
    ({ index, feedback }: { index: number; feedback: string }) => {
      const store = chatStoreRef.current;
      const state = store.getState();
      const setLintMessages = state.setLintMessages;
      setLintMessages(prev =>
        produce(prev, draft => {
          draft[index].feedback = feedback;
        }),
      );
    },
    [chatStoreRef],
  );

  const lintMultiReplicaResponses = useCallback(
    async ({
      responses,
      setHasLinterErrorForIndex,
      setHasIncompleteFrontendLinterFeedbackForIndex,
      setLintMessages,
      setParamsAtLastCheck,
    }: {
      responses: IndexedInteractionStepResponse[][]; // one response per replica
      setHasLinterErrorForIndex: (index: number, value: boolean) => void;
      setHasIncompleteFrontendLinterFeedbackForIndex: (index: number, value: boolean) => void;
      setLintMessages: (value: LintMessage[] | ((prev: LintMessage[]) => LintMessage[])) => void;
      setParamsAtLastCheck: (value: any) => void;
    }): Promise<boolean> => {
      if (responses.length === 0) {
        return true;
      }

      const store = chatStoreRef.current;
      // Get the state from the store during the call
      const state = store.getState();

      // Get the necessary infos from the state
      const taskParams = state.config;
      const allStepParams = state.stepParams;

      setLintMessages([]);

      const stepResponseLinterResultPromises: Promise<IProcessedLintResults | undefined>[] = [];

      for (let i = 0; i < responses[0].length; i++) {
        const shouldSkip = computeShouldSkipLinting({
          projectId,
          assignmentId,
          stepParams: allStepParams[i],
          stepResponse: {
            ...responses[0][i],
            context: {
              ...responses[0][i].context,
              shouldContinue: responses.reduce(
                (acc, replicaResponse) => acc || !!replicaResponse[i].context?.shouldContinue,
                false,
              ),
            },
          },
          eventBus,
          disableLinter,
        });
        if (shouldSkip) {
          stepResponseLinterResultPromises.push(Promise.resolve(undefined));
          continue;
        }

        stepResponseLinterResultPromises.push(
          API.genai.runMultiReplicaPerStepResponseLinter({
            projectId: projectId!,
            assignmentId: assignmentId!,
            stepResponses: responses.map(
              (replicaResponse: IndexedInteractionStepResponse[]) => replicaResponse[i],
            ),
            metadata: {
              taskParams,
              stepId: responses[0][i].step_id!,
              currentResponseIndex: i,
              stepParams: allStepParams[i],
              responses: [], // as to not break getTurnContext for BaseChatTaskPerStepLinter
              interactionHistory: state?.interactionHistory ?? {},
            },
          }),
        );
      }

      const stepResponseLinterResults = await Promise.all(stepResponseLinterResultPromises);

      const canSubmits: boolean[] = [];
      for (let i = 0; i < stepResponseLinterResults.length; i++) {
        if (!stepResponseLinterResults[i] || stepResponseLinterResults[i]?.lintVerdict === 'pass') {
          continue;
        }
        canSubmits.push(
          handleProcessedLintResponse({
            response: stepResponseLinterResults[i]!,
            currentResponseIndex: i,
            setHasLinterErrorForIndex,
            setHasIncompleteFrontendLinterFeedbackForIndex,

            // Concatenate lint messages across all steps for multi replica linters ony
            setLintMessages: (value: LintMessage[] | ((_: LintMessage[]) => LintMessage[])) => {
              const newLintMessages = value as LintMessage[];
              setLintMessages(prev =>
                produce(prev, draft => {
                  draft.push(...newLintMessages);
                }),
              );
            },
            eventBus,
            setParamsAtLastCheck,
          }).canSubmit,
        );
      }

      return canSubmits.length === 0 || canSubmits.every(v => v === true);
    },
    [chatStoreRef, projectId, assignmentId, eventBus, disableLinter],
  );

  const lintStepResponse = useCallback(
    async (
      stepParams: StepParamsForType<InteractionStepType>,
      stepResponse: InteractionStepResponse,
      currentResponseIndex: number,
    ) => {
      const shouldSkip = computeShouldSkipLinting({
        projectId,
        assignmentId,
        stepParams,
        stepResponse,
        eventBus,
        disableLinter,
      });

      if (shouldSkip) {
        return true;
      }

      // Initialize the chat store inside the callback
      const store = chatStoreRef.current;
      // Get the state from the store during the call
      const state = store.getState();

      // Get the necessary infos from the state
      const taskParams = state.config;
      const setIsValidating = state.setIsPerStepLinterValidating;
      const clearStepLinterError = state.clearStepLinterErrors;
      const allStepParams = state.stepParams;
      const lintMessages = state.lintMessages;
      const setLintMessages = state.setLintMessages;
      const paramsAtLastCheck = state.perStepLinterParamsAtLastCheck;
      const setParamsAtLastCheck = state.setPerStepLinterParamsAtLastCheck;
      const interactionHistory = state.interactionHistory;

      const responses = state.responses;
      const setHasLinterErrorForIndex = state.setHasLinterErrorForIndex;
      const setHasIncompleteFrontendLinterFeedbackForIndex =
        state.setHasIncompleteFrontendLinterFeedbackForIndex;

      const currentMultiChatIndex = state.currentMultiChatIndex;
      // We set it to match the UI checkboxes, which are 1-indexed
      const currentMultiChatPage = !_.isNil(currentMultiChatIndex)
        ? (currentMultiChatIndex + 1).toString()
        : undefined;

      try {
        setIsValidating(true);
        clearStepLinterError();

        const {
          sanitizedStepResponse: stepResponseForLinter,
          sanitizedResponses: stepFilteredResponses,
        } = sanitizeStepResponses({
          currStepResponse: stepResponse,
          allStepResponses: responses,
          currStepParams: stepParams,
          allStepParams,
        });

        const params = {
          projectId: projectId!,
          assignmentId: assignmentId!,
          stepResponse: stepResponseForLinter,
          metadata: {
            taskParams,
            stepId: stepParams.id,
            stepParams,
            stepTitle: stepParams.title,
            mostRecentPrompt:
              !_.isUndefined(responses) && !_.isUndefined(currentResponseIndex)
                ? getMostRecentPrompt(responses, currentResponseIndex)
                : undefined,
            mostRecentResponse:
              !_.isUndefined(responses) && !_.isUndefined(currentResponseIndex)
                ? getMostRecentResponse(responses, currentResponseIndex)
                : undefined,
            currentResponseIndex,
            responses: stepFilteredResponses,
            currentMultiChatPage,
            interactionHistory,
          },
          runTestMode: linterPlaygroundMode,
        };

        const { canSubmit, passLinters, allLinterFeedbackCompleted } = getCanSubmit(lintMessages);
        const shouldRerun = !_.isEqual(paramsAtLastCheck, params) && allLinterFeedbackCompleted;
        if (!shouldRerun) {
          if (!passLinters) setHasLinterErrorForIndex(currentResponseIndex, true);
          if (!allLinterFeedbackCompleted)
            setHasIncompleteFrontendLinterFeedbackForIndex(currentResponseIndex, true);
          if (canSubmit) {
            for (const lintMessage of lintMessages) {
              if (lintMessage.feedback) {
                logEvent(Event.GenAILinterFeedbackEvent, {
                  linterName: lintMessage.lintRuleName,
                  projectId,
                  workerId,
                  assignmentId,
                  frontendSeverity: lintMessage.severity,
                  linterFrontendFeedback: lintMessage.feedback,
                  genAILinterEventRowId: lintMessage.genAILinterEventRowId,
                } as GenAILinterFeedbackEvent);
              }
            }
            setLintMessages([]);
            setParamsAtLastCheck(undefined);
          }

          return canSubmit;
        }

        // if there were previous lint messages and associated feedback, log the feedback
        for (const lintMessage of lintMessages) {
          if (lintMessage.feedback) {
            logEvent(Event.GenAILinterFeedbackEvent, {
              linterName: lintMessage.lintRuleName,
              projectId,
              workerId,
              assignmentId,
              frontendSeverity: lintMessage.severity,
              linterFrontendFeedback: lintMessage.feedback,
              stepResponse,
              metadata: params.metadata,
              genAILinterEventRowId: lintMessage.genAILinterEventRowId,
            } as GenAILinterFeedbackEvent);
          }
        }

        setParamsAtLastCheck(params);
        const response = await API.genai.runPerStepResponseLinter(params);

        const processedLintResponse = handleProcessedLintResponse({
          response,
          currentResponseIndex,
          setHasLinterErrorForIndex,
          setHasIncompleteFrontendLinterFeedbackForIndex,
          setLintMessages,
          setParamsAtLastCheck,
          eventBus,
          assignmentId,
        });
        state.setJsonataContextList(processedLintResponse.newJsonataContextList);
        return processedLintResponse.canSubmit;
      } catch (err) {
        logger.error(`Per Step Response Linter [${projectId}]: Error running linters: [${err}]`);
        defaultApiFetchErrorHandler(err, {
          handleHttp: true,
          message: 'Something went wrong while linting the response.',
        });
      } finally {
        setIsValidating(false);
      }
    },
    [
      projectId,
      assignmentId,
      eventBus,
      disableLinter,
      chatStoreRef,
      workerId,
      linterPlaygroundMode,
    ],
  );

  const dismissError = useCallback(
    (index: number) => {
      const store = chatStoreRef.current;
      const state = store.getState();
      const lintMessages = state.lintMessages;
      const setLintMessages = state.setLintMessages;
      setLintMessages(prev =>
        produce(prev, draft => {
          draft[index].dismissed = true;
        }),
      );
      eventBus.emit('linter:dismissed', {
        index,
      });
      logEvent(Event.LinterDismissEvent, {
        projectId,
        workerId,
        linterName: lintMessages[index].lintRuleName,
      } as LinterDismissEvent);
    },
    [projectId, workerId, chatStoreRef, eventBus],
  );

  useEffect(() => {
    const subscription = eventBus.on('linter:dismiss').subscribe(payload => {
      const index = payload.data.index;
      dismissError(index);
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [eventBus, dismissError]);

  return {
    dismissError,
    lintStepResponse,
    setFeedback,
    lintMultiReplicaResponses,
  };
};
